import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { CollectionUpdateRequestDto } from '../../../store/landscapeAPI/collection/dto/request/collection-update-request.dto';
import { CollectionResponseDto } from '../../../store/landscapeAPI/collection/dto/response/collection-response.dto';
import {
  fetchCollectionById,
  updateCollection,
} from '../../../store/landscapeAPI/collection/reducers/collection.slice';
import { AppDispatch, RootState } from '../../../store/store';
import Spinner from '../../atoms/Spinner/Spinner';
import { ReportingDetailsCollectionsEditTemplate } from '../../templates';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import { showSuccess } from '../../../utils/Notifications';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';

// import styles from './ReportingDetailsCollectionsEditPage.module.css';

interface ReportingDetailsCollectionsEditPageProps {}

const ReportingDetailsCollectionsEditPage: FC<
  ReportingDetailsCollectionsEditPageProps
> = () => {
  const [collection, setCollection] = useState<CollectionResponseDto>();
  const [imageMeta, setImageMeta] = useState<ImageMeta>();

  const { id } = useParams();

  const navigate = useNavigate();

  const { status, error } = useSelector(
    (state: RootState) => state.collectionSlice
  );

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const dispatch = useDispatch<AppDispatch>();

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!collection) return;
    const params: TranslateParams<CollectionResponseDto> = {
      obj: collection,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as CollectionResponseDto;

    const updateRequest: CollectionUpdateRequestDto = {
      name: translatedResponse.name,
      id: collection.id,
      icon: collection.icon,
      published: collection.published,
    };

    await dispatch(
      updateCollection({
        updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchCollectionById({ id: id, language: selectedLanguage }))
        .unwrap()
        .then((response: CollectionResponseDto) => setCollection(response));
    }
  }, [id, dispatch, selectedLanguage]);

  const handleOnChange = (collection: CollectionResponseDto) => {
    setCollection(collection);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = (await dispatch(
      saveMedia(formData)
    ).unwrap()) as MediaSaveResponseDto;
    return response;
  };

  const handleEditCollection = async (collection: CollectionResponseDto) => {
    const icon = await processMedia(collection.icon ?? '', imageMeta);
    const data: CollectionUpdateRequestDto = {
      id: collection.id,
      name: collection.name,
      icon: icon.href,
      published: collection.published,
    };

    await dispatch(
      updateCollection({ updateRequest: data, language: selectedLanguage })
    );

    showSuccess('Collection saved successfully.');
    navigate('/reporting-details/collections');
  };

  if (
    status === 'loading' ||
    mediaStatus === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  if (status === 'failed') {
    return <div>{error}</div>;
  }

  return (
    <>
      {collection && (
        <ReportingDetailsCollectionsEditTemplate
          data={collection}
          handleEditCollection={handleEditCollection}
          handleOnChange={handleOnChange}
          handleImageOnChange={handleImageOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </>
  );
};

export default ReportingDetailsCollectionsEditPage;
